import React, { useState } from 'react';
import { useBudget } from './BudgetContext';
import { Button } from '../ui/Button';
import styles from './BulkActionBar.module.css';

export const BulkActionBar: React.FC = () => {
  const {
    categories,
    selectedIds,
    bulkUpdateTransactions,
    bulkDeleteTransactions,
    exitSelectMode,
  } = useBudget();

  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const selectedCount = selectedIds.size;

  if (selectedCount === 0) {
    return null;
  }

  const expenseCategories = categories.filter(c => c.type === 'expense');
  const incomeCategories = categories.filter(c => c.type === 'income');
  const transferCategories = categories.filter(c => c.type === 'transfer');

  const handleCategorize = async (categoryId: string | null) => {
    setIsProcessing(true);
    try {
      await bulkUpdateTransactions(Array.from(selectedIds), categoryId);
    } finally {
      setIsProcessing(false);
      setShowCategoryDropdown(false);
    }
  };

  const handleDelete = async () => {
    setIsProcessing(true);
    try {
      await bulkDeleteTransactions(Array.from(selectedIds));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className={styles.bulkActionBar}>
      <div className={styles.selectionInfo}>
        <span className={styles.selectedCount}>
          {selectedCount} transaction{selectedCount !== 1 ? 's' : ''} selected
        </span>
      </div>

      <div className={styles.actions}>
        {/* Category dropdown */}
        <div className={styles.categoryAction}>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
            disabled={isProcessing}
            leftIcon={
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 3H8V5H2V3Z" fill="currentColor"/>
                <path d="M2 7H6V9H2V7Z" fill="currentColor"/>
                <path d="M2 11H4V13H2V11Z" fill="currentColor"/>
                <path d="M12 7L16 11L12 15V12H9V10H12V7Z" fill="currentColor"/>
              </svg>
            }
          >
            Set Category
          </Button>

          {showCategoryDropdown && (
            <div className={styles.categoryDropdown}>
              <button
                className={styles.categoryOption}
                onClick={() => handleCategorize(null)}
              >
                <span className={styles.categoryColor} style={{ backgroundColor: '#8B949E' }} />
                Uncategorized
              </button>
              
              {expenseCategories.length > 0 && (
                <>
                  <div className={styles.categoryGroup}>Expenses</div>
                  {expenseCategories.map((cat) => (
                    <button
                      key={cat.id}
                      className={styles.categoryOption}
                      onClick={() => handleCategorize(cat.id)}
                    >
                      <span className={styles.categoryColor} style={{ backgroundColor: cat.color }} />
                      {cat.name}
                    </button>
                  ))}
                </>
              )}
              
              {incomeCategories.length > 0 && (
                <>
                  <div className={styles.categoryGroup}>Income</div>
                  {incomeCategories.map((cat) => (
                    <button
                      key={cat.id}
                      className={styles.categoryOption}
                      onClick={() => handleCategorize(cat.id)}
                    >
                      <span className={styles.categoryColor} style={{ backgroundColor: cat.color }} />
                      {cat.name}
                    </button>
                  ))}
                </>
              )}
              
              {transferCategories.length > 0 && (
                <>
                  <div className={styles.categoryGroup}>Transfers</div>
                  {transferCategories.map((cat) => (
                    <button
                      key={cat.id}
                      className={styles.categoryOption}
                      onClick={() => handleCategorize(cat.id)}
                    >
                      <span className={styles.categoryColor} style={{ backgroundColor: cat.color }} />
                      {cat.name}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Delete button */}
        <Button
          variant="danger"
          size="sm"
          onClick={handleDelete}
          disabled={isProcessing}
          leftIcon={
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.5 7V12H6.5V7H5.5ZM9.5 7V12H10.5V7H9.5Z" fill="currentColor"/>
              <path d="M2 3V4H3V14C3 14.2652 3.10536 14.5196 3.29289 14.7071C3.48043 14.8946 3.73478 15 4 15H12C12.2652 15 12.5196 14.8946 12.7071 14.7071C12.8946 14.5196 13 14.2652 13 14V4H14V3H2ZM4 14V4H12V14H4Z" fill="currentColor"/>
              <path d="M6 1H10V2H6V1Z" fill="currentColor"/>
            </svg>
          }
        >
          Delete
        </Button>

        {/* Cancel button */}
        <Button
          variant="secondary"
          size="sm"
          onClick={exitSelectMode}
          disabled={isProcessing}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default BulkActionBar;
